import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { locationsApi, customerAddressesApi } from '../services/api';
import type { CreateCustomerAddressDto, UpdateCustomerAddressDto } from '../types/locations';

// Keys
export const locationKeys = {
  countries: ['countries'] as const,
  provinces: (countryId: string) => ['provinces', countryId] as const,
  cities: (provinceId: string) => ['cities', provinceId] as const,
  customerAddresses: (customerId: string) => ['customerAddresses', customerId] as const,
};

// Hooks for Static Location Data (Long/Infinite Cache)
export const useCountries = () => {
  return useQuery({
    queryKey: locationKeys.countries,
    queryFn: locationsApi.getCountries,
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    gcTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
  });
};

export const useProvinces = (countryId: string | null) => {
  return useQuery({
    queryKey: locationKeys.provinces(countryId!),
    queryFn: () => locationsApi.getProvincesByCountry(countryId!),
    enabled: !!countryId, // Only fetch if countryId is present
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });
};

export const useCities = (provinceId: string | null) => {
  return useQuery({
    queryKey: locationKeys.cities(provinceId!),
    queryFn: () => locationsApi.getCitiesByProvince(provinceId!),
    enabled: !!provinceId, // Only fetch if provinceId is present
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  });
};

// Hooks for Customer Addresses
export const useCustomerAddresses = (customerId: string) => {
  return useQuery({
    queryKey: locationKeys.customerAddresses(customerId),
    queryFn: () => customerAddressesApi.getAll(customerId),
    enabled: !!customerId,
  });
};

export const useCustomerAddressesMutations = (customerId: string) => {
  const queryClient = useQueryClient();
  const keys = locationKeys.customerAddresses(customerId);

  const createMutation = useMutation({
    mutationFn: (data: CreateCustomerAddressDto) => customerAddressesApi.create(customerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomerAddressDto }) => 
      customerAddressesApi.update(customerId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customerAddressesApi.delete(customerId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => customerAddressesApi.setDefault(customerId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys });
    },
  });

  return {
    create: createMutation,
    update: updateMutation,
    delete: deleteMutation,
    setDefault: setDefaultMutation,
  };
};
